import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["open", "measuredHeight", "transitioning"],
  _excluded2 = ["children", "Content", "renderContent", "duration", "onOpenStart", "onCloseStart", "onOpenComplete", "onCloseComplete", "open"];
import PropTypes from 'prop-types';
import { PureComponent } from 'react';
import { findDOMNode } from 'react-dom';
import styled from 'styled-components';
import { ACCORDION_TRANSITION_TIMING } from 'HubStyleTokens/times';
import TwoWayTransition from './TwoWayTransition';
import { memoizedSequence } from '../core/Functions';
import { jsx as _jsx } from "react/jsx-runtime";
const transitionTime = parseInt(ACCORDION_TRANSITION_TIMING, 10);

/** @internal */

/**
 * Makes isRequired props with defaultProps values optional to correct types in
 * returned TwoWayTransition component.
 *
 * @internal
 **/

/** @internal */

/** @internal */
export const AccordionTransitionWrapper = styled(_ref => {
  let {
      open,
      transitioning
    } = _ref,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  return /*#__PURE__*/_jsx(TwoWayTransition.PlainWrapper, Object.assign({
    "data-open-complete": open && !transitioning
  }, rest));
}).withConfig({
  displayName: "AccordionTransition__AccordionTransitionWrapper",
  componentId: "becxq-0"
})(["transition:", ";height:", ";opacity:", ";pointer-events:", ";transform:", ";overflow:", ";"], ({
  duration,
  measuredHeight,
  transitionActive
}) => measuredHeight != null && transitionActive ? `height ${duration}ms cubic-bezier(0.25, 0.1, 0.25, 1),
    opacity ${duration}ms cubic-bezier(0.42, 0, 0.58, 1),
    transform ${duration}ms cubic-bezier(0.42, 0, 0.58, 1)` : 'none', ({
  open,
  measuredHeight,
  transitioning
}) => transitioning && (open ? `${measuredHeight}px` : 0), ({
  open,
  transitioning
}) => transitioning && (open ? 1 : 0), ({
  transitioning
}) => transitioning && 'none', ({
  open,
  transitioning
}) => transitioning && `translateY(${open ? '0' : '-10px'})`, ({
  open,
  transitioning
}) => !open || transitioning ? 'hidden' : 'visible');
class AccordionTransition extends PureComponent {
  static getDerivedStateFromProps(props, state) {
    let derivedState = null;
    if (state.prevOpen !== props.open) {
      derivedState = {
        prevOpen: props.open
      };
      if (!state.transitioning) {
        derivedState = Object.assign({}, derivedState, {
          measuredHeight: null
        });
      }
    }
    return derivedState;
  }
  constructor(props) {
    super(props);
    this.handleTransitionStart = () => {
      this.setState({
        transitioning: true
      });
    };
    this.handleTransitionComplete = () => {
      this.setState({
        transitioning: false
      });
    };
    this.state = {
      measuredHeight: 0,
      // 0 = not needed, null = needed
      transitioning: false,
      prevOpen: props.open
    };
  }
  componentDidUpdate() {
    if (this.state.measuredHeight == null) {
      this.measureHeight();
    }
  }
  measureHeight() {
    const el = findDOMNode(this);
    if (!el) return;
    this.setState({
      measuredHeight: el.scrollHeight
    });
  }
  render() {
    const _ref2 = this.props,
      {
        children,
        Content,
        renderContent,
        duration,
        onOpenStart,
        onCloseStart,
        onOpenComplete,
        onCloseComplete,
        open
      } = _ref2,
      rest = _objectWithoutPropertiesLoose(_ref2, _excluded2);
    let renderedContent = null;
    if (open) {
      if (Content) {
        renderedContent = /*#__PURE__*/_jsx(Content, {});
      } else if (renderContent) {
        renderedContent = renderContent();
      } else {
        renderedContent = children;
      }
    }
    return /*#__PURE__*/_jsx(TwoWayTransition, Object.assign({}, rest, {
      duration: duration,
      measuredHeight: this.state.measuredHeight,
      onOpenStart: memoizedSequence(this.handleTransitionStart, onOpenStart),
      onCloseStart: memoizedSequence(this.handleTransitionStart, onCloseStart),
      onOpenComplete: memoizedSequence(this.handleTransitionComplete, onOpenComplete),
      onCloseComplete: memoizedSequence(this.handleTransitionComplete, onCloseComplete),
      children: renderedContent
    }));
  }
}
const propTypes = {
  children: PropTypes.node,
  Content: PropTypes.elementType,
  renderContent: PropTypes.func,
  duration: PropTypes.number.isRequired,
  onCloseComplete: PropTypes.func,
  onCloseStart: PropTypes.func,
  onOpenComplete: PropTypes.func,
  onOpenStart: PropTypes.func,
  open: PropTypes.bool,
  Wrapper: PropTypes.elementType.isRequired
};
const defaultProps = {
  duration: transitionTime,
  Wrapper: AccordionTransitionWrapper
};
AccordionTransition.propTypes = propTypes;
AccordionTransition.defaultProps = defaultProps;
AccordionTransition.displayName = 'AccordionTransition';
export default AccordionTransition;